import { useContext } from "react";
import { useLocation } from "react-router-dom";
import AuthContext from "./store/AuthContext";
import useLastRoute from "./hooks/useLastRoute";
import Footer from "./components/Layout/Footer/Footer";
import bgBridge from "./assets/images&icons/bg-5.jpg";
import bgLogin from "./assets/images&icons/bg-login.jpg";
import bgSettings from "./assets/images&icons/bg-login.jpg";
import { publicLegacyPaths } from "./routes/PublicLegacyRoutes";
import bgManageFirm from "./assets/images&icons/bg-manageFirm.png";
import bgObjectives from "./assets/images&icons/BackgroundBoardAboutUs.jpg";
import bgBenefits from "./assets/images&icons/BackgroundBenefitsAboutUs.jpg";
import bgBoard from "./assets/images&icons/BackgroundBoardAboutUs.jpg";
import bgJoin from "./assets/images&icons/BackgroundBenefitsAboutUs.jpg";
import bgPublicFirm from "./assets/images&icons/bg-20.jpg";
import bgMemberSearch from "./assets/images&icons/bg-9.jpg";
import { useStaffMode } from "./hooks/StaffModeProvider";
import { isAuthLayoutRoute } from "./utils/authLayoutRoutes";
import { ADVANCED_MEMBER_SEARCH_PATH } from "./utils/memberSearchNavigation";

const DEFAULT_HERO_OVERLAY = "rgba(55, 60, 65, 0.6)";
const ADVANCED_MEMBER_SEARCH_PAGE_FILL = "#f6f6f6";

type BackgroundRule = {
  patterns: string[];
  image: string;
  height?: string;
  overlay?: string;
  bottomFill?: string;
  backgroundPosition?: string;
};

interface IMain {
  children: React.ReactNode;
}

const Main: React.FC<IMain> = ({ children }) => {
  const { isAuthenticated, isAuthTransitionActive } = useContext(AuthContext);
  const { isStaffMode } = useStaffMode();
  const location = useLocation();
  const onAuthLayoutRoute = isAuthLayoutRoute(location.pathname);
  const usePublicContentLayout =
    !isAuthenticated || onAuthLayoutRoute || isAuthTransitionActive;

  useLastRoute();

  const backgroundRules: BackgroundRule[] = [
      {
        patterns: ["/aboutus", "/contact"],
        image: bgManageFirm,
      },
      {
        patterns: ["/objectives"],
        image: bgObjectives,
      },
      {
        patterns: ["/benefits"],
        image: bgBenefits,
      },
      {
        patterns: ["/board", "/councils"],
        image: bgBoard,
      },
      {
        patterns: ["/join"],
        image: bgJoin,
      },
      {
        patterns: ["/profile2"],
        image: bgManageFirm,
      },
      {
        patterns: ["/member2"],
        image: bgPublicFirm,
      },
      {
        patterns: [ADVANCED_MEMBER_SEARCH_PATH],
        image: bgBridge,
        height: "50vh",
        overlay: "rgba(255, 255, 255, 0.5)",
        bottomFill: ADVANCED_MEMBER_SEARCH_PAGE_FILL,
        backgroundPosition: "center top",
      },
      {
        patterns: ["/member-search"],
        image: bgMemberSearch,
      },
      {
        patterns: [
          ...publicLegacyPaths,
          "/two-factor-acknowledgement",
          "/auth/google/callback",
          "/auth/linkedin/callback",
          "/link-account-login",
          "/newUser",
          "/settings",
          "/login",
          "/forgot-password",
          "/reset-password"
        ],
        image: bgLogin,
        height: "100vh",
      },
      {
        patterns: ["/settings"],
        image: bgSettings,
      },
    ];

  const matchesPattern = (path: string, pattern: string): boolean => {
    const p = pattern.toLowerCase();
    if (p.endsWith("/*")) {
      const base = p.slice(0, -2);
      return path === base || path.startsWith(base + "/");
    }
    if (p.endsWith("*")) {
      const base = p.slice(0, -1);
      return path.startsWith(base);
    }
    return path === p || path.startsWith(p + "/");
  };

  const buildBackgroundStyle = (rule: BackgroundRule) => ({
    height: rule.height ?? "60vh",
    backgroundImage: `url(${rule.image})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: rule.backgroundPosition ?? "center top",
  });

  const pathname = location.pathname.toLowerCase();
  const activeBackgroundRule =
    backgroundRules.find((rule) =>
      rule.patterns.some((pattern) => matchesPattern(pathname, pattern)),
    ) ?? null;

  const bgStyle = activeBackgroundRule
    ? buildBackgroundStyle(activeBackgroundRule)
    : buildBackgroundStyle({ patterns: [], image: bgBridge });
  const bgHeight = bgStyle.height;
  const heroOverlay =
    activeBackgroundRule?.overlay ?? DEFAULT_HERO_OVERLAY;

  return (
    <div
      id="content-container"
      className="flex-1 w-full flex relative overflow-x-hidden overflow-y-auto flex-col min-h-0"
      style={
        activeBackgroundRule?.bottomFill
          ? { backgroundColor: activeBackgroundRule.bottomFill }
          : undefined
      }
    >
      <div className="absolute top-0 left-0 w-full z-1" style={bgStyle} />
      <div
        className="absolute top-0 left-0 w-full z-2"
        style={{
          height: bgHeight,
          backgroundColor: heroOverlay,
        }}
      />
      {activeBackgroundRule?.bottomFill ? (
        <div
          className="absolute left-0 right-0 bottom-0 z-1"
          style={{
            top: bgHeight,
            backgroundColor: activeBackgroundRule.bottomFill,
          }}
          aria-hidden="true"
        />
      ) : null}
      <div className="flex-1 flex flex-col relative z-10">
        {usePublicContentLayout ? (
          <div className={`px-12 w-full h-full my-12 flex flex-col z-10 text-start relative self-center max-w-[110rem]`}>
            {children}
          </div>
        ) : (
          <div className={`my-12 mx-auto pr-4 pl-16 md:pr-6 md:pl-[6.5rem] lg:pr-10 lg:pl-28 text-start relative w-full ${!isStaffMode ? 'max-w-[110rem]' : ''}`}>
            {children}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Main;
